import type { IWrappedProperty } from '@witkac/jschema'
import { computed, type ComputedRef, getCurrentInstance, onMounted, ref, type Ref } from 'vue'

import { confirmService } from '../services/confirm.service'
import type { Section } from '../typings/section'

function traverseTree(cmp: any, result: any[]) {
  if (cmp.component) {
    cmp = cmp.component
  }

  if (cmp.subTree?.component) {
    cmp = cmp.subTree.component
  }

  const children = cmp.subTree ? cmp.subTree.children : cmp.children
  if (!children) {
    return result
  }

  if (typeof children === 'string') {
    return result
  }

  for (let i = 0; i < children.length; i++) {
    const child = children[i]
    const model: IWrappedProperty = child.props?.model

    if (model) {
      const validationResult = model.$validate()
      if (validationResult) {
        result.push(validationResult)
      }
    }

    if (model) {
      traverseTree(child, result)
    } else {
      traverseTree(child, result)
    }
  }
  return result
}

export function useSections<T extends IWrappedProperty>(
  schema: T,
  sections: Section[],
): {
  isFirstIndex: ComputedRef<boolean>
  isLastIndex: ComputedRef<boolean>
  sections: Ref<Section[]>
  selectedSection: Ref<Section>
  nextSection: () => any
  prevSection: () => any
  setSection: (section: Section) => any
} {
  let $: any
  const instance = getCurrentInstance()
  const sectionsRef = ref(isTabDisabled())
  const selectedSection = ref(sectionsRef.value[0])

  onMounted(() => {
    $ = getCurrentInstance()
  })

  function isTabDisabled() {
    if (!schema.$external.tabsEnabled.length) {
      return sections
    }
    return sections.map((section, index) => {
      section.tabDisabled = !schema.$external.tabsEnabled.includes(index)
      return { ...section }
    })
  }

  function nextSection() {
    const index = sectionsRef.value.findIndex((section) => section === selectedSection.value)
    if (index < 0 || index + 1 >= sectionsRef.value.length) {
      return
    }

    setSection(sectionsRef.value[index + 1])
  }

  function prevSection() {
    const index = sectionsRef.value.findIndex((section) => section === selectedSection.value)

    if (index < 0 || index - 1 < 0) {
      return
    }

    setSection(sectionsRef.value[index - 1])
  }

  function validateCurrentSection(): IWrappedProperty[] {
    const result: any[] = []

    traverseTree($, result)
    return result
  }

  function setSection(section: Section) {
    if (section === selectedSection.value) {
      return
    }
    const errors = validateCurrentSection()
    instance?.emit('validate', errors)

    if (errors.length === 0) {
      selectedSection.value = section
      instance?.emit('section-changed', section)
      return
    }

    return confirmService
      .confirm(
        'Nieprawidłowo wypełniono pola w tej części wniosku. Czy chcesz przejść dalej, a błędy poprawić później?',
      )
      .then(() => (selectedSection.value = section))
      .then(() => instance?.emit('section-changed', section))
      .then(() => window.scrollTo(0, 0))
  }

  return {
    isFirstIndex: computed(() => sectionsRef.value[0] === selectedSection.value),
    isLastIndex: computed(
      () => sectionsRef.value[sectionsRef.value.length - 1] === selectedSection.value,
    ),
    sections: sectionsRef,
    selectedSection,

    nextSection,
    prevSection,
    setSection,
  }
}
